<template>
	<div class="d-inline-flex">
		<v-menu location="bottom end">
			<template #activator="{ props }">
				<v-btn
					v-bind="props"
					icon="mdi-dots-vertical"
					size="small"
					variant="text"
					:disabled="disabled"
					aria-label="More actions"
					@click.stop
				/>
			</template>
			<v-list density="compact">
				<v-list-item
					v-if="canDownload"
					prepend-icon="mdi-download"
					title="Download (JSON)"
					@click="download"
				/>
				<v-list-item
					v-if="canExportExcel"
					prepend-icon="mdi-file-excel"
					title="Export XLSX"
					@click="exportExcel"
				/>
				<v-list-item
					prepend-icon="mdi-magnify"
					title="Tree view"
					@click="goToDataViewer"
				/>
				<v-list-item
					v-if="useExtractionEventHistory"
					prepend-icon="mdi-history"
					title="History"
					@click="showHistory"
				/>
				<v-list-item
					v-if="canReopen"
					prepend-icon="mdi-account-arrow-left-outline"
					title="Reopen"
					@click="reopenDialog = true"
				/>
			</v-list>
		</v-menu>

		<!-- Reopen confirm. Curator surface: reopen only, never reassign - reassign
		of a completed paper stays on the manager ProjectView (DCTL-2135, Eric). -->
		<v-dialog
			v-model="reopenDialog"
			max-width="520"
			transition="fade-transition"
		>
			<v-card>
				<v-toolbar color="primary" flat>
					<v-toolbar-title>Re-open Source Data Extraction</v-toolbar-title>
				</v-toolbar>
				<v-card-text class="px-6 pt-4 pb-0">
					<div class="text-body-1 mb-2">
						Re-open
						<span class="font-weight-bold">{{
							paperName || "this extraction"
						}}</span
						>?
					</div>
					<div class="text-body-2 text-grey-darken-2">
						It will move back to rework and leave My Completed until it is
						marked completed again.
					</div>
				</v-card-text>
				<v-card-actions class="py-4">
					<v-spacer />
					<v-btn
						class="bg-secondary"
						elevation="2"
						:loading="reopening"
						@click="reopen"
					>
						Confirm
					</v-btn>
					<v-btn
						class="bg-grey"
						elevation="2"
						:disabled="reopening"
						@click="reopenDialog = false"
					>
						Cancel
					</v-btn>
					<v-spacer />
				</v-card-actions>
			</v-card>
		</v-dialog>

		<extraction-history-modal
			v-if="useExtractionEventHistory"
			ref="historyModal"
			:data-entry-uuid="dataEntryUuid"
		/>
	</div>
</template>

<script>
import { mapActions } from "pinia";
import { useLDFlag } from "launchdarkly-vue-client-sdk";

import ExtractionHistoryModal from "@/components/ExtractionHistoryModal";
import { useMainStore } from "@/store";
import { filenameFromContentDisposition } from "@/utils/contentDisposition";

// DCTL-2135: the completed-extraction action set, surfaced as an overflow menu
// so it can hang off a kanban card (SDE "My Completed") as well as anywhere else
// that has a single completed data-entry uuid. Mirrors the endpoints, gates and
// filename logic of CompletedExtractionTable exactly; that page should later be
// refactored to consume this component (dedupe follow-up). Read-only view stays
// a separate card affordance (SdeKanbanCard's View button, DCTL-2132).
export default {
	name: "CompletedExtractionActions",

	components: {
		ExtractionHistoryModal,
	},

	props: {
		// The requester's completed extraction to act on (unambiguous since
		// DCTL-2136 made the board's completed column user-level).
		dataEntryUuid: {
			type: String,
			required: true,
		},
		// Used only for the download/export filename and the reopen confirm copy.
		paperName: {
			type: String,
			default: "",
		},
		// The extraction's owner (user UUID). A plain reopen must keep the same
		// owner, so the reopen call sends this as the reassignUserUuid target;
		// the backend resolves it to the owner id, so moveAssignment early-returns
		// (toUserId === fromUserId) instead of reassigning. Defaults to the current
		// user, which is correct for the SDE "My Completed" card (the extraction is
		// always mine); a future consumer acting on someone else's extraction MUST
		// pass the real owner's uuid.
		ownerUserUuid: {
			type: String,
			default: null,
		},
		disabled: {
			type: Boolean,
			default: false,
		},
	},

	emits: ["reopened"],

	setup() {
		// History is behind the same LD flag CompletedExtractionTable gates it on.
		const useExtractionEventHistory = useLDFlag("extraction-event-history");
		return { useExtractionEventHistory };
	},

	data() {
		return {
			reopenDialog: false,
			reopening: false,
		};
	},

	computed: {
		// Gates mirror CompletedExtractionTable / the My Completed Extractions page
		// verbatim - grant NO new access (DCTL-2135, Eric).
		canDownload() {
			return useMainStore().can("download_data");
		},

		// DCTL-2238: the XLSX export follows the permissions that already grant
		// access to the extraction (mirrors the widened POST /data-entry/export-xlsx
		// gate). Deliberately separate from canDownload so opening up the
		// spreadsheet does not also surface the raw-JSON download.
		canExportExcel() {
			return useMainStore().canAny([
				"download_data",
				"source_data_extraction",
				"quality_control",
			]);
		},
		canReopen() {
			return useMainStore().can("access_completed_extractions");
		},
	},

	methods: {
		...mapActions(useMainStore, ["showNotification"]),

		download() {
			this.$http
				.post(`/data-entry/download`, { uuids: [this.dataEntryUuid] })
				.then((response) => {
					if (response.data.status === "error") {
						// Invalid extraction: the full per-field validation table
						// lives on the Completed Extractions page; point the user
						// there rather than reproduce it on a card.
						this.showNotification({
							message:
								"This extraction has validation issues; open it from Completed Extractions to review.",
							status: "error",
						});
					} else {
						this.downloadJson(response.data.data);
					}
				})
				.catch((error) =>
					this.showNotification(
						error.response?.data || {
							message: "Could not download the extraction.",
							status: "error",
						},
					),
				);
		},

		exportExcel() {
			this.$http
				.post(
					`/data-entry/export-xlsx`,
					{ uuids: [this.dataEntryUuid] },
					{ responseType: "blob" },
				)
				.then((response) => {
					const filename =
						filenameFromContentDisposition(
							response.headers["content-disposition"],
						) || `${this.safeName()}.xlsx`;
					this.triggerBlobDownload(response.data, filename);
				})
				.catch(async (error) => {
					// A blob error body must be read back as text before showing.
					let payload = error.response?.data;
					if (payload instanceof Blob) {
						try {
							payload = JSON.parse(await payload.text());
						} catch {
							payload = { message: "Failed to export Excel" };
						}
					}
					this.showNotification(
						payload || {
							message: "Failed to export Excel",
							status: "error",
						},
					);
				});
		},

		downloadJson(jsonData) {
			const blob = new Blob([JSON.stringify(jsonData)], {
				type: "application/json",
			});
			this.triggerBlobDownload(blob, `${this.safeName()}.json`);
		},

		triggerBlobDownload(blob, filename) {
			const url = URL.createObjectURL(blob);
			const link = document.createElement("a");
			link.href = url;
			link.download = filename;
			document.body.appendChild(link);
			link.click();
			document.body.removeChild(link);
			URL.revokeObjectURL(url);
		},

		// Filename sanitisation mirrors CompletedExtractionTable.downloadJson.
		safeName() {
			let name = (this.paperName || "extraction").replace(/[<>:"/\\|?*]/g, "-");
			if (name.length > 250) name = name.substring(0, 250);
			return name;
		},

		goToDataViewer() {
			this.$router.push({
				path: "/data-viewer",
				query: { sdeUuid: this.dataEntryUuid },
			});
		},

		showHistory() {
			this.$nextTick(() => this.$refs.historyModal.open());
		},

		reopen() {
			this.reopening = true;
			// Plain reopen, no reassign: send the owner's UUID so the backend
			// resolves it to the owner id and moveAssignment early-returns instead
			// of reassigning (DCTL-2137). Omitting it would 400.
			const ownerUuid = this.ownerUserUuid ?? useMainStore().user.uuid;
			this.$http
				.post(`/data-entry/${this.dataEntryUuid}/reopen`, {
					reassignUserUuid: ownerUuid,
				})
				.then(() => {
					this.reopenDialog = false;
					this.showNotification({
						message: "Successfully re-opened source data extraction",
						status: "success",
					});
					this.$emit("reopened");
				})
				.catch((error) =>
					this.showNotification(
						error.response?.data || {
							message: "Could not re-open the extraction.",
							status: "error",
						},
					),
				)
				.finally(() => {
					this.reopening = false;
				});
		},
	},
};
</script>
